import React, {useState, useEffect, Fragment} from 'react';
import {Controller, get, useWatch} from 'react-hook-form';
import SubHeader from 'app/gakki/components/SubHeader';
import Select from 'components/Select';
import {transferDateObj, formatYYYYMMDD} from 'app/gakki/utils/time';
import {
  FormItem,
  Input,
  DatePicker,
  ErrorMessage,
  Switch,
} from 'components/form-items';
import {DateTime} from 'luxon';
import Block from './block';
import Loading from './loading';
import Show from './show';

const nationality = [
  {
    label: '本國人',
    value: 0,
  },
  {
    label: '外籍',
    value: 1,
  },
  {
    label: '外籍配偶',
    value: 2,
  },
  {
    label: '大陸配偶',
    value: 3,
  },
];

const yesorno = [
  {
    label: '是',
    value: true,
  },
  {
    label: '否',
    value: false,
  },
];
const LaborInsuranceData = (props: any) => {
  const {
    formProps,
    defaultValues,
    options,
    onBoardingDate,
    getInsuranceAmount,
    updateLocale,
    defaultEmployeeId,
    presetEffectiveDate,
    shouldPresetEffectiveDate,
  } = props;
  const {control, formState, errors, setValue, trigger} = formProps;

  const isUnable = !useWatch({
    control,
    name: `laborInsurance.enabled`,
    defaultValue: defaultValues.enabled,
  });
  const isDeduction = useWatch({
    control,
    name: `laborInsurance.isDeduction`,
    defaultValue: defaultValues.isDeduction,
  });
  const effectiveDate = useWatch({
    control,
    name: `laborInsurance.effectiveDate`,
    defaultValue: defaultValues.effectiveDate,
  });
  const insuranceIdentityId = useWatch({
    control,
    name: `laborInsurance.insuranceIdentityId`,
    defaultValue: defaultValues.insuranceIdentityId,
  });
  const employeeId = useWatch({
    control,
    name: `employee.id`,
    defaultValue: defaultEmployeeId,
  });
  const [insuranceAmount, setInsuranceAmount] = useState([]);
  const [isInsuranceAmountFetching, setIsInsuranceAmountFetching] =
    useState(false);
  const isError = (name: string) =>
    !isUnable && formState.isSubmitted && get(errors, name);
  const renderErrorMessage = (name: string) =>
    isError(name) && <ErrorMessage>{get(errors, name)?.message}</ErrorMessage>;

  useEffect(() => {
    if (!isUnable && formState.isSubmitted) {
      trigger();
    }
  }, [isUnable, formState.isSubmitted]);
  useEffect(() => {
    if (
      defaultValues.effectiveDate &&
      defaultValues.insuranceIdentityId &&
      employeeId
    ) {
      setIsInsuranceAmountFetching(true);
      getInsuranceAmount({
        amountLevelType: 0,
        insuranceAmountLevelId: undefined,
        effectiveDate: defaultValues.effectiveDate,
        insuranceIdentityId: defaultValues.insuranceIdentityId,
      })
        .then((res: any) => {
          setInsuranceAmount(res.data);
        })
        .finally(() => {
          setIsInsuranceAmountFetching(false);
        });
    }
  }, [defaultValues.effectiveDate, defaultValues.insuranceIdentityId, employeeId]);

  const updateInsuranceAmount = ({
    effectiveDateInput,
    insuranceIdentityIdInput,
  }: {
    effectiveDateInput?: string;
    insuranceIdentityIdInput?: string;
  }) => {
    if (
      (effectiveDateInput || effectiveDate) &&
      (insuranceIdentityIdInput || insuranceIdentityId)
    ) {
      setIsInsuranceAmountFetching(true);
      setInsuranceAmount([]);
      getInsuranceAmount({
        amountLevelType: 0,
        insuranceAmountLevelId: undefined,
        effectiveDate: effectiveDateInput || effectiveDate,
        insuranceIdentityId: insuranceIdentityIdInput || insuranceIdentityId,
      })
        .then((res: any) => {
          setInsuranceAmount(res.data);
        })
        .finally(() => {
          setIsInsuranceAmountFetching(false);
        });
    }
  };
  return (
    <div data-test-id={'labor-insurance'}>
      <SubHeader>
        <span style={{paddingRight: '15px'}}>勞保</span>
        <Controller
          render={({onChange, value}) => (
            <div data-test-id={'enable-labor-insurance'} className="flex items-center">
              <Switch
                checked={value}
                onChange={(e: any) => {
                  onChange(e);
                  updateLocale();
                }}
              />
            </div>
          )}
          name={`laborInsurance.enabled`}
          control={control}
          defaultValue={defaultValues.enabled}
        />
      </SubHeader>
      <Block>
        <Show isShow>
          <FormItem
            label="生效日期"
            itemName="laborInsurance_effectiveDate"
            required
            width="160px"
            data-test-id={'effective-date'}
          >
            <Controller
              render={({onChange, value}) => (
                <Fragment>
                  <DatePicker
                    dateFormat="yyyy/MM/dd"
                    minDate={transferDateObj(onBoardingDate)}
                    selected={transferDateObj(value)}
                    disabled={formState.isSubmitting || isUnable}
                    onChange={(date: Date) => {
                      const dateString = formatYYYYMMDD(date);
                      onChange(dateString);
                      setValue(`laborInsurance.insuranceAmountLevelId`, null);
                      updateInsuranceAmount({effectiveDateInput: dateString});
                      updateLocale();
                    }}
                    onBlur={() => {
                      if (value && shouldPresetEffectiveDate === 'true') {
                        presetEffectiveDate(value)
                      }
                    }}
                    onCalendarClose={() => {
                      if (value && shouldPresetEffectiveDate === 'true') {
                        presetEffectiveDate(value)
                      }
                    }}
                    error={isError(`laborInsurance.effectiveDate`)}
                  />
                  {renderErrorMessage(`laborInsurance.effectiveDate`)}
                </Fragment>
              )}
              name={`laborInsurance.effectiveDate`}
              control={control}
              rules={{
                required: !isUnable && '生效日期為必填',
                validate: {
                  isAfterOnBoardingDate: v => {
                    let onBoardingDateObj = transferDateObj(onBoardingDate);
                    let effectiveDateObj = transferDateObj(v);
                    if (!isUnable && onBoardingDateObj && effectiveDateObj) {
                      return (
                        DateTime.fromJSDate(onBoardingDateObj) <=
                          DateTime.fromJSDate(effectiveDateObj) ||
                        `不可早於到職日${onBoardingDate}`
                      );
                    }
                    return true;
                  },
                },
              }}
              defaultValue={defaultValues.effectiveDate}
            />
          </FormItem>
          <FormItem
            label="籍別"
            itemName="laborInsurance_nationality"
            required
            width="120px"
            data-test-id={'nationality'}
          >
            <Controller
              render={({onChange, value}) => (
                <Select
                  value={nationality.find(o => o.value === value)}
                  options={nationality}
                  onChange={(o: {value: string}) => {
                    onChange(o.value);
                    updateLocale();
                  }}
                  isDisabled={formState.isSubmitting || isUnable}
                />
              )}
              name={`laborInsurance.nationality`}
              control={control}
              defaultValue={defaultValues.nationality}
            />
          </FormItem>
          <FormItem
            label="投保單位"
            itemName="laborInsurance_insuranceApplicantUnitId"
            required
            width="200px"
            data-test-id={'applicant-unit-id'}
          >
            <Controller
              render={({onChange, value}) => (
                <Select
                  value={
                    options.insuranceUnits.find(
                      (o: any) => o.insuranceApplicantUnitId === value,
                    ) || null
                  }
                  options={options.insuranceUnits}
                  onChange={(o: any) => {
                    onChange(o.insuranceApplicantUnitId);
                    updateLocale();
                  }}
                  isDisabled={formState.isSubmitting || isUnable}
                  getOptionLabel={(o: any) =>
                    `${o.insuranceNo} ${o.insuranceApplicantUnitName}`
                  }
                  getOptionValue={(o: any) => o.insuranceApplicantUnitId}
                  error={isError(`laborInsurance.insuranceApplicantUnitId`)}
                />
              )}
              name={`laborInsurance.insuranceApplicantUnitId`}
              control={control}
              rules={{required: !isUnable && '投保單位為必填'}}
              defaultValue={defaultValues.insuranceApplicantUnitId}
            />
          </FormItem>
          <FormItem
            label="投保身分"
            itemName="laborInsurance_insuranceIdentityId"
            required
            width="200px"
            data-test-id={'insurance-identity-id'}
          >
            <Controller
              render={({onChange, value}) => (
                <Select
                  value={
                    options.insuranceIdentity.find(
                      (o: any) => o.value === value,
                    ) || null
                  }
                  options={options.insuranceIdentity}
                  onChange={(o: {value: string}) => {
                    onChange(o.value);
                    setValue(`laborInsurance.insuranceAmountLevelId`, null);
                    updateInsuranceAmount({insuranceIdentityIdInput: o.value});
                    updateLocale();
                  }}
                  isDisabled={formState.isSubmitting || isUnable}
                  getOptionLabel={(o: any) => o.item}
                  error={isError(`laborInsurance.insuranceIdentityId`)}
                />
              )}
              name={`laborInsurance.insuranceIdentityId`}
              control={control}
              rules={{required: !isUnable && '投保身分為必填'}}
              defaultValue={defaultValues.insuranceIdentityId}
            />
          </FormItem>
          <FormItem
            label="投保金額"
            itemName="laborInsurance_insuranceAmountLevelId"
            required
            width="135px"
            data-test-id={'insurance-amount-level-id'}
          >
            <Controller
              render={({onChange, value}) => (
                <Select
                  isLoading={isInsuranceAmountFetching}
                  loadingMessage={() => '載入中...'}
                  value={
                    insuranceAmount.find((o: any) => o.value === value) || ''
                  }
                  options={insuranceAmount}
                  onChange={(o: {value: string}) => {
                    onChange(o.value);
                    updateLocale();
                  }}
                  onMenuOpen={() => {
                    if (insuranceAmount.length === 0) {
                      updateInsuranceAmount({});
                    }
                  }}
                  isDisabled={formState.isSubmitting || isUnable}
                  getOptionLabel={(o: any) => o.item}
                  error={isError(`laborInsurance.insuranceAmountLevelId`)}
                />
              )}
              name={`laborInsurance.insuranceAmountLevelId`}
              control={control}
              rules={{required: !isUnable && '投保金額為必填'}}
              defaultValue={defaultValues.insuranceAmountLevelId}
            />
          </FormItem>
          <FormItem
            label="減免身分"
            itemName="laborInsurance_isDeduction"
            required
            width="80px"
            data-test-id={'is-deduction'}
          >
            <Controller
              render={({onChange, value}) => (
                <Select
                  value={yesorno.find(o => o.value === value)}
                  options={yesorno}
                  onChange={(o: {value: string}) => {
                    onChange(o.value);
                    updateLocale();
                  }}
                  isDisabled={formState.isSubmitting || isUnable}
                />
              )}
              name={`laborInsurance.isDeduction`}
              control={control}
              defaultValue={defaultValues.isDeduction}
            />
          </FormItem>
          <FormItem
            label="補助費率"
            itemName="laborInsurance_allowanceRate"
            required
            width="100px"
            hidden={!isDeduction}
            data-test-id={'allowance-rate'}
          >
            <Controller
              render={({onChange, value}) => (
                <Input
                  value={value}
                  disabled={formState.isSubmitting || isUnable}
                  onChange={e => {
                    const {value} = e.target;
                    if (
                      !value ||
                      /^(\d+(\.\d{0,2})?|\.?\d{1,2})$/.test(value)
                    ) {
                      onChange(value);
                      updateLocale();
                    }
                  }}
                  error={isDeduction && isError(`laborInsurance.allowanceRate`)}
                />
              )}
              name={`laborInsurance.allowanceRate`}
              control={control}
              rules={{
                required: isDeduction && !isUnable && '補助費率為必填',
                min: {
                  value: 0,
                  message: '最小值為0',
                },
                max: {
                  value: 100,
                  message: '最大值為100',
                },
              }}
              defaultValue={defaultValues.allowanceRate}
            />
            {renderErrorMessage(`laborInsurance.allowanceRate`)}
          </FormItem>
          <FormItem
            label="減免備註"
            itemName="laborInsurance_allowanceRemark"
            width="135px"
            hidden={!isDeduction}
            data-test-id={'allowance-remark'}
          >
            <Controller
              render={({onChange, value}) => (
                <Input
                  value={value}
                  disabled={formState.isSubmitting || isUnable}
                  onChange={(e: any) => {
                    onChange(e);
                    updateLocale();
                  }}
                  maxLength={250}
                />
              )}
              name={`laborInsurance.allowanceRemark`}
              control={control}
              defaultValue={defaultValues.allowanceRemark}
            />
          </FormItem>
        </Show>
      </Block>
    </div>
  );
};

export default LaborInsuranceData;
